import axios from 'axios'
import type {
  PasswordChangeRequest,
  PasswordChangeResponse,
  ClearFolderCacheRequest,
  ClearFolderCacheResponse,
  RebuildFolderCacheRequest,
  RebuildFolderCacheResponse,
} from '../../types/admin/settings'
import type { CacheStatsResponse } from '../../types/carbon/models/CacheStatsResponse'
import { CacheStatsResponseFromJSON } from '../../types/carbon/models/CacheStatsResponse'

const getHeaders = (sessionId: string): Record<string, string> => {
  return {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'x-session-id': sessionId,
  }
}

export interface ClearCacheResponse {
  tableCacheCount?: number
  tableAnalysisCacheCount?: number
  analysisCacheCount?: number
  blobCacheCleared?: boolean
  carbonCacheCleared?: number
}

export interface ClearCacheRequest {
  customer: string
  job?: string
  target?: 'all' | 'vartree'
}

export interface RebuildMetadataResponse {
  message: string
  customer: string
  job: string
}

export interface RebuildMetadataRequest {
  customer: string
  job: string
}

// VTR validation types
export interface VtrIssue {
  type: 'orphaned_item' | 'duplicate_folder' | 'depth_jump'
  line: number
  description: string
  itemName: string
  depth: number
}

export interface VtrValidationResult {
  issues: VtrIssue[]
  isValid: boolean
  itemCount: number
}

export interface VtrFixResult {
  message: string
  fixed: VtrIssue[]
  unfixable: VtrIssue[]
}

/**
 * Clear cache for a customer (all jobs) or specific job
 */
export const clearCacheRequest = async (
  sessionId: string,
  params: ClearCacheRequest
): Promise<ClearCacheResponse> => {
  const response = await axios.post<ClearCacheResponse>(
    '/api/v1/system/clear-cache',
    params,
    {
      headers: getHeaders(sessionId),
    }
  )
  return response.data
}

/**
 * Rebuild metadata for a job (triggers metadata indexing without clearing cache)
 */
export const rebuildJobMetadata = async (
  sessionId: string,
  params: RebuildMetadataRequest
): Promise<RebuildMetadataResponse> => {
  const response = await axios.post<RebuildMetadataResponse>(
    '/api/v1/system/rebuild-metadata',
    params,
    {
      headers: getHeaders(sessionId),
    }
  )
  return response.data
}

/**
 * Change user password
 */
export const changePasswordRequest = async (
  sessionId: string,
  params: PasswordChangeRequest
): Promise<PasswordChangeResponse> => {
  const response = await axios.post<PasswordChangeResponse>(
    '/api/v1/user/password/change',
    params,
    {
      headers: getHeaders(sessionId),
    }
  )
  return response.data
}

// Notification types
export interface Notification {
  id: string
  created_at: number
  customer: string
  job: string
  notification_type: string
  message: string
}

/**
 * Get recent notifications for a customer with a specific type
 */
export const getRecentNotifications = async (
  sessionId: string,
  customer: string,
  type: string,
  limit: number = 10
): Promise<Notification[]> => {
  const response = await axios.get<Notification[]>('/api/v1/notifications', {
    params: { customer, type, limit },
    headers: getHeaders(sessionId),
  })
  return response.data || []
}

/**
 * Get notifications for a specific job (all types)
 */
export const getJobNotifications = async (
  sessionId: string,
  customer: string,
  job: string,
  limit: number = 20
): Promise<Notification[]> => {
  const response = await axios.get<Notification[]>('/api/v1/notifications', {
    params: { customer, job, limit },
    headers: getHeaders(sessionId),
  })
  return response.data || []
}

/**
 * Get all notifications for a customer (all jobs, all types)
 */
export const getCustomerNotifications = async (
  sessionId: string,
  customer: string,
  limit: number = 50
): Promise<Notification[]> => {
  const response = await axios.get<Notification[]>('/api/v1/notifications', {
    params: { customer, limit },
    headers: getHeaders(sessionId),
  })
  return response.data || []
}

/**
 * Get Carbon's in-memory variable cache statistics
 */
export const getCarbonCacheStats = async (
  sessionId: string
): Promise<CacheStatsResponse> => {
  const response = await axios.get('/api/v1/system/carbon-cache-stats', {
    headers: getHeaders(sessionId),
  })
  return CacheStatsResponseFromJSON(response.data)
}

/**
 * Clear folder cache - deletes all table_caches and analysis_caches for tables under the folder path
 */
export const clearFolderCache = async (
  sessionId: string,
  params: ClearFolderCacheRequest
): Promise<ClearFolderCacheResponse> => {
  const response = await axios.delete<ClearFolderCacheResponse>(
    `/api/v1/tables/${encodeURIComponent(params.customer)}/${encodeURIComponent(params.job)}/folder-cache`,
    {
      params: { path: params.folderPath },
      headers: getHeaders(sessionId),
    }
  )
  return response.data
}

/**
 * Rebuild folder cache - clears cache and enqueues worker jobs to regenerate cache for each table
 */
export const rebuildFolderCache = async (
  sessionId: string,
  params: RebuildFolderCacheRequest
): Promise<RebuildFolderCacheResponse> => {
  const response = await axios.post<RebuildFolderCacheResponse>(
    `/api/v1/tables/${encodeURIComponent(params.customer)}/${encodeURIComponent(params.job)}/folder-cache/rebuild`,
    { tablePaths: params.tablePaths },
    {
      params: { path: params.folderPath },
      headers: getHeaders(sessionId),
    }
  )
  return response.data
}

/**
 * Validate a job's VTR file for structural issues
 */
export const validateVtr = async (
  sessionId: string,
  customer: string,
  job: string
): Promise<VtrValidationResult> => {
  const response = await axios.get<VtrValidationResult>(
    `/api/v1/job/${encodeURIComponent(customer)}/${encodeURIComponent(job)}/vtr/validate`,
    { headers: getHeaders(sessionId) }
  )
  return response.data
}

/**
 * Index a job for vector search (metadata + embeddings)
 */
export const indexJobForSearch = async (
  sessionId: string,
  params: { customer: string; job: string }
): Promise<{ message: string }> => {
  const response = await axios.post(
    `/api/v1/job/${encodeURIComponent(params.customer)}/${encodeURIComponent(params.job)}/index`,
    {},
    { headers: getHeaders(sessionId) }
  )
  return response.data
}

/**
 * Fix structural issues in a job's VTR file
 */
export const fixVtr = async (
  sessionId: string,
  customer: string,
  job: string
): Promise<VtrFixResult> => {
  const response = await axios.post<VtrFixResult>(
    `/api/v1/job/${encodeURIComponent(customer)}/${encodeURIComponent(job)}/vtr/fix`,
    {},
    { headers: getHeaders(sessionId) }
  )
  return response.data
}
